import React, { useState, useEffect, useRef } from 'react'

import { UserFilter, DateRangeFilter, SortOrder } from '../FilterTypes'

type Props = {
    stage: string
    criteriaCreatedDate?: DateRangeFilter

    onClosePopup: (value: boolean) => void
    onAcceptFilter: () => void

    // Фильтрация по столбцу "НМЦД"
    onChangeFilterSettingCriteriaCreatedDateSort: (value: SortOrder) => void
    onChangeFilterSettingCriteriaCreatedDate: (
        valueFrom: string | null,
        valueTo: string | null
    ) => void
}

const PopupMenuDateCreated: React.FC<Props> = (props) => {
    const [dateFrom, setDateFrom] = useState<string>(
        props.criteriaCreatedDate?.from == null
            ? ''
            : props.criteriaCreatedDate?.from
    )
    const [dateTo, setDateTo] = useState<string>(
        props.criteriaCreatedDate?.to == null
            ? ''
            : props.criteriaCreatedDate?.to
    )
    // Флаг, указывающий, что пользователь изменил значения
    const [isModified, setIsModified] = useState(false)

    // Флаг, чтобы пропустить выполнение эффекта при первом рендере
    const isFirstRender = useRef(true)

    // Дебаунс: вызываем функцию через 1 секунду после того, как пользователь перестал вводить
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        // Обновление родительского состояния происходит, только если пользователь изменял данные
        if (!isModified) return

        const timer = setTimeout(() => {
            props.onChangeFilterSettingCriteriaCreatedDate(
                dateFrom.trim() === '' ? null : dateFrom,
                dateTo.trim() === '' ? null : dateTo
            )
            // Сброс флага после обновления
            setIsModified(false)
        }, 1000)

        return () => clearTimeout(timer)
    }, [dateFrom, dateTo, isModified])

    return (
        <div className="absolute right-0 my-2 w-fit origin-top-right rounded-[10px] shadow-md shadow-[#18181830] z-10 bg-[#d9dce0] dark:bg-[#333033] border border-[#b8b8b8] dark:border-[#696769]">
            <div className="p-1">
                {/* Блок с чекбоксами */}

                <div className="flex flex-row items-center">
                    <label className="flex flex-row items-center rounded-lg gap-1 w-44 ">
                        <input
                            type="date"
                            className="form-input focus:outline-none focus:ring-1 focus:ring-[#181818] dark:focus:ring-[#FFFFFF] w-full "
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value)
                                setIsModified(true)
                            }}
                        />
                    </label>
                    <div className="flex w-fit h-full px-1 items-center justify-center text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                        <svg
                            className="w-3 h-3"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="minus"
                        >
                            <path
                                fill="#8A8A8A"
                                d="M19,11H5a1,1,0,0,0,0,2H19a1,1,0,0,0,0-2Z"
                            ></path>
                        </svg>
                    </div>
                    <label className="flex flex-row items-center rounded-lg gap-1 w-44">
                        <input
                            type="date"
                            className="form-input focus:outline-none focus:ring-1 focus:ring-[#181818] dark:focus:ring-[#FFFFFF] w-full"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value)
                                setIsModified(true)
                            }}
                        />
                    </label>
                </div>

                <div className="flex flex-row w-full px-4 text-[11px] text-left items-center justify-end mt-1">
                    <label
                        className="w-full text-[11px] text-right text-sky-600 hover:text-sky-400 hover:underline cursor-pointer"
                        onClick={() => {
                            setDateFrom('')
                            setDateTo('')
                            setIsModified(true)
                            props.onChangeFilterSettingCriteriaCreatedDate(
                                null,
                                null
                            )
                        }}
                    >
                        Сбросить
                    </label>
                </div>

                {/* Разделитель */}
                <hr className="mt-3 mx-4 border-[#b8b8b8] dark:border-[#464447]" />

                <div className="flex flex-col w-full gap-1 mt-3">
                    {/* Заголовок сортировки и кнопки сортировки */}
                    <div className="px-4 text-[11px] text-left text-[#6A6A6A] dark:text-[#8A8A8A]">
                        Сортировка
                    </div>
                    {/* Разделитель */}
                    <button
                        className="flex flex-row w-full text-left p-1 hover:bg-[#e9e9e9] dark:hover:bg-[#656365] text-[12.8px] text-[#181818] dark:text-[#FFFFFF] rounded-lg items-center"
                        onClick={() => {
                            props.onChangeFilterSettingCriteriaCreatedDateSort(
                                'asc'
                            )
                        }}
                    >
                        <svg
                            className="w-3 h-3"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="signal-alt"
                        >
                            <g transform="rotate(90 12 12)">
                                <path
                                    fill={`${props.criteriaCreatedDate?.sort_order == 'asc' ? '#0284c7' : '#8A8A8A'}`}
                                    d="M10,14a1,1,0,0,0-1,1v6a1,1,0,0,0,2,0V15A1,1,0,0,0,10,14ZM5,18a1,1,0,0,0-1,1v2a1,1,0,0,0,2,0V19A1,1,0,0,0,5,18ZM20,2a1,1,0,0,0-1,1V21a1,1,0,0,0,2,0V3A1,1,0,0,0,20,2ZM15,9a1,1,0,0,0-1,1V21a1,1,0,0,0,2,0V10A1,1,0,0,0,15,9Z"
                                ></path>
                            </g>
                        </svg>
                        <span className="ml-2 text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                            По возрастанию
                        </span>
                    </button>
                    <button
                        className="flex flex-row w-full text-left p-1 hover:bg-[#e9e9e9] dark:hover:bg-[#656365] text-[12.8px] text-[#181818] dark:text-[#FFFFFF] rounded-lg items-center"
                        onClick={() => {
                            props.onChangeFilterSettingCriteriaCreatedDateSort(
                                'desc'
                            )
                        }}
                    >
                        <svg
                            className="w-3 h-3"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="signal-alt"
                        >
                            <g transform="translate(24,0) scale(-1,1)">
                                <g transform="rotate(-90 12 12)">
                                    <path
                                        fill={`${props.criteriaCreatedDate?.sort_order == 'desc' ? '#0284c7' : '#8A8A8A'}`}
                                        d="M10,14a1,1,0,0,0-1,1v6a1,1,0,0,0,2,0V15A1,1,0,0,0,10,14ZM5,18a1,1,0,0,0-1,1v2a1,1,0,0,0,2,0V19A1,1,0,0,0,5,18ZM20,2a1,1,0,0,0-1,1V21a1,1,0,0,0,2,0V3A1,1,0,0,0,20,2ZM15,9a1,1,0,0,0-1,1V21a1,1,0,0,0,2,0V10A1,1,0,0,0,15,9Z"
                                    ></path>
                                </g>
                            </g>
                        </svg>
                        <span className="ml-2 text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                            По убыванию
                        </span>
                    </button>
                </div>

                <div className="flex w-full px-4 text-[11px] text-left items-center justify-end">
                    <label
                        className="w-fit text-[11px] text-left text-sky-600 hover:text-sky-400 hover:underline cursor-pointer"
                        onClick={() => {
                            props.onChangeFilterSettingCriteriaCreatedDateSort(
                                null
                            )
                        }}
                    >
                        Сбросить
                    </label>
                </div>

                <div className="flex flex-col w-full mt-3 items-start justify-center">
                    <button
                        className="flex h-6 flex-col w-full gap-1 text-left text-[12.8px] rounded-[10px] items-center justify-center shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#F6F6F6] hover:bg-[#FFFFFF] dark:bg-[#494849] dark:hover:bg-[#656365]"
                        onClick={() => {
                            props.onAcceptFilter()
                            props.onClosePopup(false)
                        }}
                    >
                        Сохранить
                    </button>
                </div>
            </div>
        </div>
    )
}

export default PopupMenuDateCreated
